import "./EducationalContentAndVideosGuide.scss";

import { Card, Flex, RadioGroup, Text } from "@fluentui/react-northstar";
import { TelemetryEventTypes } from "@teams/types";
import React, { Fragment, useMemo, useState } from "react";

import { useUserInfo } from "../actions/useUserInfo";
import { clientEventTracker } from "../utils/clientTelemetry";
import { PollyIcon } from "./PollyIcon";

type TabType = "team" | "chat" | "meeting";

type VideoTabItem = { label: string; value: TabType; videoUrl: string; action: string };

const tabItems: VideoTabItem[] = [
  {
    label: "Chat",
    value: "chat",
    videoUrl: "ZtB74AgzO2c",
    action: "vid tab chat clicked",
  },
  {
    label: "Meeting",
    value: "meeting",

    videoUrl: "JoiKCNmLb6I",
    action: "vid tab meeting clicked",
  },
  {
    label: "Team",
    value: "team",

    videoUrl: "PgLpX_atWPM",
    action: "vid tab team clicked",
  },
];

export const EducationalContentAndVideosGuide = () => {
  const [selectedTab, setSelectedTab] = useState<VideoTabItem>(tabItems[0]);

  const radioOptionItems = useMemo(() => {
    return tabItems.map(tabItem => {
      return {
        label: "Meeting",
        value: "meeting",
        name: "video-tabs",
        key: tabItem.value,
        children: () => (
          <TabItemElement tab={tabItem} selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
        ),
      };
    });
  }, [selectedTab]);

  return (
    <div className="container">
      <Flex className="educational-content-guide" hAlign="center" column gap="gap.large">
        <div className="educational-content-guide__content-wrapper">
          <div className="educational-content-guide__video">
            <iframe
              width={720}
              height={405}
              src={`https://www.youtube.com/embed/${selectedTab.videoUrl}?controls=0`}
              title="YouTube video player"
              frameBorder={0}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>

          <RadioGroup
            vertical={false}
            items={radioOptionItems}
            checkedValue={selectedTab.value}
            className="educational-content-guide__tab-group"
          />
          <PersonalTabFooter />
        </div>
      </Flex>
    </div>
  );
};

const TabItemElement: React.FC<{
  tab: VideoTabItem;
  selectedTab: VideoTabItem;
  setSelectedTab: React.Dispatch<React.SetStateAction<VideoTabItem>>;
}> = ({ tab, selectedTab, setSelectedTab }) => {
  return (
    <Card
      aria-roledescription="tab"
      onClick={() => setSelectedTab(tab)}
      selected={tab.value === selectedTab.value}
      className={`educational-content-guide__tab-group-item ${
        tab.value === selectedTab.value && "selected"
      }`}
    >
      <Card.Header fitted>
        <Flex gap="gap.medium" vAlign="center">
          <PollyIcon
            icon="youtube-play-icon"
            inheritColor
            size="xl"
            className="educational-content-guide__polly-icon"
          />

          <Flex column>
            <Text
              size="medium"
              content="Adding Polly"
              className="educational-content-guide__video-title"
            />
            <Text
              size="medium"
              weight="bold"
              content={`to a ${tab.label}`}
              className="educational-content-guide__video-sub-title"
            />
          </Flex>
        </Flex>
      </Card.Header>
    </Card>
  );
};

const PersonalTabFooter = () => {
  const { user } = useUserInfo();

  const links: Array<{ url: string; label: string; event: TelemetryEventTypes }> = [
    {
      label: "Help Center",
      url: "https://www.polly.ai/help/microsoft-teams",
      event: "help center clicked",
    },
    { label: "Email Support", url: "https://www.polly.ai/contact", event: "email support clicked" },
    {
      label: "Talk to Sales",
      url: "https://www.polly.ai/talk-to-sales",
      event: "ContactSales clicked",
    },
  ];

  return (
    <Flex gap="gap.small" className="educational-content-guide__footer-links">
      <Text size="large" weight="semibold" content="Need more Help?" className="footer-text" />
      {links.map((link, i) => (
        <Fragment key={link.label}>
          {i !== 0 && <Text size="large" weight="regular" content="|" className="link-separator" />}

          <a
            href={link.url}
            className="brand-link footer-text"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              clientEventTracker.track(link.event, user, {
                "2021Q2PersonalTab": user.featureFlags.Test_2021Q2PersonalTab,
              });
            }}
          >
            <Text size="large" weight="semibold" content={link.label} className="footer-link" />
          </a>
        </Fragment>
      ))}
    </Flex>
  );
};
